import React from 'react';
import { Box, Card, CardContent, Typography, Stack, Button } from '@mui/material';

interface Props {
  primary: string;
  successColor: string;
  warningColor: string;
  errorColor: string;
  chartColors: string[];
}

// Live preview of the user's branding choices. Pure-CSS bars (no chart lib)
// keeps it cheap and avoids re-rendering charting code on every keystroke.
export default function BrandPreviewCard({
  primary,
  successColor,
  warningColor,
  errorColor,
  chartColors,
}: Props) {
  // Deterministic but visually-varied bar heights so the preview chart
  // doesn't look like a uniform block.
  const bars = React.useMemo(
    () => chartColors.map((_, i) => 32 + ((i * 23) % 68)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chartColors.length],
  );

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 3 },
          '&:last-child': { pb: { xs: 2, sm: 3 } },
        }}
      >
        <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
          Live Preview
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Updates as you change colors above. Click Save to apply across the app.
        </Typography>

        {/* Buttons + semantic chips */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ rowGap: 1 }}>
            <Button
              variant="contained"
              sx={{
                bgcolor: primary,
                '&:hover': { bgcolor: primary, filter: 'brightness(0.92)' },
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
              }}
            >
              Primary Action
            </Button>
            <Button
              variant="outlined"
              sx={{
                borderColor: primary,
                color: primary,
                '&:hover': { borderColor: primary, bgcolor: `${primary}10` },
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              Secondary
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ ml: { sm: 'auto' } }} flexWrap="wrap" rowGap={1}>
            <SemanticPill label="Success" color={successColor} />
            <SemanticPill label="Warning" color={warningColor} />
            <SemanticPill label="Error" color={errorColor} />
          </Stack>
        </Stack>

        {/* Mini chart preview */}
        <Box
          sx={(t) => ({
            p: 2,
            borderRadius: `${t.ds.radius.sm}px`,
            bgcolor: t.tokens.surface.sunken,
            border: `1px solid ${t.tokens.border.subtle}`,
          })}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              mb: 1.5,
              fontWeight: 700,
              fontSize: '0.6875rem',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Chart palette preview
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75, height: 92 }}>
            {chartColors.map((color, i) => (
              <Box
                key={i}
                title={color}
                sx={{
                  flex: 1,
                  height: `${bars[i]}%`,
                  bgcolor: color,
                  borderRadius: '3px 3px 0 0',
                  minHeight: 8,
                  transition: 'background-color 120ms ease',
                }}
              />
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

// Plain styled pill so the user's exact branding color shows through.
// We can't use <Chip> here because the global MuiChip theme override
// (`&.MuiChip-colorDefault`) has higher specificity than `sx={{ bgcolor }}`
// and would wash out the chosen color.
function SemanticPill({ label, color }: { label: string; color: string }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 22,
        px: 1,
        borderRadius: 999,
        bgcolor: color,
        color: '#fff',
        fontWeight: 600,
        fontSize: '0.75rem',
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
}
